import { Link } from 'react-router-dom';
import SEO from '../../components/seo/SEO';
import SchemaMarkup from '../../components/seo/SchemaMarkup';
import { useState } from 'react';
import { useLanguageLink } from '../../hooks/useLanguageLink';

export default function BlogMLBotDetectionPage() {
  const { createLink } = useLanguageLink();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'What is the difference between rule-based and ML-based bot detection?',
      answer: 'Rule-based systems block traffic that matches a predefined list of known bad patterns — specific IPs, user-agents, or behaviors. They are fast but static: as soon as bots change their signatures, the rules miss them. ML-based systems learn from millions of behavioral signals and continuously adapt. They detect bots they have never explicitly seen before by recognizing that something "feels wrong" statistically, even when every individual signal looks normal.',
    },
    {
      question: 'Can bots really fool machine learning detection?',
      answer: 'Sophisticated bots do attempt to mimic human behavior: simulating mouse movement, randomizing click timing, rotating residential IPs, and spoofing browser fingerprints. However, ML systems analyze hundreds of features simultaneously — session entropy, navigation patterns, timing micro-variations, device consistency across signals — making holistic impersonation extremely difficult. Each added deception layer introduces its own statistical anomaly that trained models pick up.',
    },
    {
      question: 'Does ML bot detection slow down my landing pages?',
      answer: 'No. Axetra\'s detection engine runs server-side as a lightweight filter in front of your traffic. The decision to allow or redirect a visitor happens in under 10 milliseconds on average. End users experience no measurable latency because the filter responds before the page even begins to load.',
    },
    {
      question: 'How does behavioral fingerprinting differ from IP blacklisting?',
      answer: 'IP blacklisting blocks specific addresses or ranges. Bots easily bypass it by rotating through residential proxy pools containing millions of clean IPs. Behavioral fingerprinting ignores the IP address and instead evaluates how the visitor interacts — timing, scroll velocity, input patterns, hardware consistency. A clean IP operated by a bot still fails behavioral checks; a flagged IP used by a real person passes.',
    },
    {
      question: 'How quickly does Axetra\'s model adapt to new bot techniques?',
      answer: 'Axetra collects anonymized behavioral signals from every request processed across all campaigns. New bot patterns that emerge — even from zero-day scripts targeting fresh verticals — create detectable statistical anomalies in the aggregated data. Model updates incorporate these signals continuously, so detection improves automatically without requiring users to configure anything.',
    },
    {
      question: 'What kind of traffic does Axetra classify beyond simple bots?',
      answer: 'Beyond obvious bots, Axetra classifies residential proxies used by click farms, VPN exit nodes, data center ranges, browser automation frameworks (Puppeteer, Playwright, Selenium), browser extensions that inject automated clicks, and low-quality human traffic from incentivized click networks. Each category can be handled differently — blocked, redirected, or flagged for review.',
    },
  ];

  const publishDate = '2026-05-12';

  return (
    <>
      <SEO
        title="How Machine Learning Detects Bots Better Than Rules (2026 Guide)"
        description="Learn how ML-powered bot detection outperforms rule-based filters. Discover the behavioral signals, anomaly detection, and adaptive models Axetra uses to classify suspicious traffic with over 99% accuracy."
        canonical="/blog/machine-learning-bot-detection"
        keywords="machine learning bot detection, ML traffic filtering, bot detection AI, suspicious traffic detection, behavioral fingerprinting, bot vs human traffic, click fraud detection, automated traffic"
        ogImage="/images/og-blog-ml-bot-detection.jpg"
        ogType="article"
        publishedTime={publishDate}
        modifiedTime={publishDate}
      />

      <SchemaMarkup
        type="article"
        headline="How Machine Learning Detects Bots Better Than Rules (2026 Guide)"
        author="Axetra Team"
        datePublished={publishDate}
        dateModified={publishDate}
        image="/images/og-blog-ml-bot-detection.jpg"
      />

      <SchemaMarkup type="faq" questions={faqs} />

      <article className="min-h-screen">
        {/* Article Header */}
        <header className="bg-gradient-to-br from-primary-50 via-white to-purple-50 py-16">
          <div className="container-marketing max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold">
                Technology
              </span>
              <time className="text-gray-600" dateTime={publishDate}>
                May 12, 2026
              </time>
              <span className="text-gray-400">•</span>
              <span className="text-gray-600">12 min read</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              How Machine Learning Detects Bots Better Than Rule-Based Systems
            </h1>

            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              Traditional blacklists and static rules are losing the arms race against modern bots. Here's how ML-powered behavioral analysis catches what rules miss — and how Axetra uses it to protect your campaigns.
            </p>

            <div className="mt-8 flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                K
              </div>
              <div>
                <div className="font-semibold text-gray-900">Axetra Team</div>
                <div className="text-gray-600 text-sm">Traffic Filtering Experts</div>
              </div>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <div className="bg-white py-16">
          <div className="container-marketing max-w-4xl prose prose-lg max-w-none">

            {/* Introduction */}
            <section className="mb-12">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Every year, advertisers lose billions of dollars to bot traffic. Click farms drain ad budgets. Scrapers steal content. Automated scripts skew analytics, corrupt A/B tests, and get Facebook ad accounts suspended. For years, the industry's answer was the same: build a bigger blacklist.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                That answer no longer works. Modern bots don't come from known data centers or use obvious user-agent strings. They run on residential proxies, rotate identities, and simulate human behavior well enough to defeat simple pattern matching. <strong>Blocking them with rules is like trying to stop water with a net.</strong>
              </p>
              <p className="text-xl text-gray-700 leading-relaxed">
                Machine learning changes the game. Instead of asking "does this visitor match a known bad pattern?", ML asks "does this visitor behave like a real person?". That shift — from signature-matching to behavioral understanding — is why ML-based detection catches bots that rules cannot.
              </p>
            </section>

            {/* Why Rules Fail */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Why Rule-Based Detection Is Losing the Arms Race
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Rule-based systems work on a simple principle: define what bad traffic looks like, then block it. Block this IP range. Block that user-agent. Block requests with no referrer. For a few years in the early 2010s, this was enough.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The problem is that bots evolved faster than rule maintainers. Today's sophisticated bots:
              </p>

              <ul className="space-y-3 text-lg text-gray-700 mb-8">
                {[
                  { label: 'Rotate through residential IPs', desc: 'sourced from real consumer devices through proxy networks, making IP blacklists useless' },
                  { label: 'Spoof legitimate browser fingerprints', desc: 'mimicking Chrome on Windows with real screen resolutions and font sets' },
                  { label: 'Simulate mouse movement and scrolling', desc: 'using pre-recorded human interaction patterns to pass basic behavioral checks' },
                  { label: 'Vary click timing and session length', desc: 'avoiding obviously inhuman speed patterns that simple rules catch' },
                  { label: 'Use real TLS fingerprints', desc: 'so network-level analysis cannot distinguish them from genuine browsers' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span><strong>{item.label}</strong> — {item.desc}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6">
                <p className="text-gray-700">
                  <strong>The core problem:</strong> Every time a new rule is published, bot operators update their scripts to avoid it. Rule lists are always catching yesterday's bots. Machine learning generalizes from patterns rather than memorizing specific signatures — so it catches tomorrow's bots too.
                </p>
              </div>
            </section>

            {/* How ML Works */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                How Machine Learning Approaches Bot Detection
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                ML bot detection trains models on labeled datasets of real human sessions and known bot sessions. The model learns which combinations of signals are statistically associated with each class. At inference time — when a new visitor arrives — the model evaluates hundreds of features and outputs a probability score.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The key insight is that no single signal is conclusive. A bot can fake any one feature. But faking all of them simultaneously, in a way that produces a naturally human-looking joint distribution, is exponentially harder. ML exploits this.
              </p>

              {/* Feature categories */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                {[
                  {
                    title: 'Network & Identity Signals',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ),
                    from: 'from-blue-500',
                    to: 'to-blue-700',
                    desc: 'Verifies the true origin of every request by evaluating IP reputation, network type, and geographic consistency.',
                  },
                  {
                    title: 'Browser & Device Fingerprint',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    ),
                    from: 'from-emerald-500',
                    to: 'to-emerald-700',
                    desc: 'Identifies the real device behind each request, detecting automation tools and headless browsers regardless of spoofed headers.',
                  },
                  {
                    title: 'Behavioral Timing Analysis',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    ),
                    from: 'from-violet-500',
                    to: 'to-violet-700',
                    desc: 'Measures mouse movement, scroll patterns, and engagement depth to distinguish a live user from a script.',
                  },
                  {
                    title: 'Traffic Pattern Signals',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    ),
                    from: 'from-orange-500',
                    to: 'to-orange-600',
                    desc: 'Tracks request cadence, referrer chains, and cross-session consistency to surface coordinated bot activity.',
                  },
                ].map((group) => (
                  <div key={group.title} className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                    <div className={`bg-gradient-to-r ${group.from} ${group.to} px-5 py-4 flex items-center gap-3`}>
                      <div className="text-white opacity-90">{group.icon}</div>
                      <h3 className="font-semibold text-white text-base tracking-tight">{group.title}</h3>
                    </div>
                    <div className="bg-white px-5 py-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{group.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6">
                <p className="text-gray-700 italic">
                  <strong>Why this matters:</strong> A bot may successfully fake its browser fingerprint. But if its behavioral timing is robotic, or its network signals contradict its claimed location, the model still classifies it correctly. No single evasion is sufficient.
                </p>
              </div>
            </section>

            {/* Anomaly Detection */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Anomaly Detection: Catching Bots We've Never Seen Before
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                One of the most powerful aspects of ML is <strong>anomaly detection</strong> — the ability to flag traffic as suspicious even when it doesn't match any known bot signature. This matters enormously in practice because new bot techniques appear constantly.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Anomaly detection models learn the statistical distribution of normal human traffic. When a visitor's behavioral profile falls outside that distribution — even in subtle, hard-to-articulate ways — the model assigns a high anomaly score. This catches zero-day bot scripts, novel click farms, and custom-built automation tools before anyone has written a rule for them.
              </p>

              <div className="bg-gray-50 rounded-xl p-8 mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Real Example: The "Too Perfect" Bot</h3>
                <p className="text-gray-700 mb-4">
                  A click farm operator trains bots to mimic human behavior: random delays, realistic mouse paths, scroll events. Against a rule-based system, these bots pass perfectly. Against an anomaly detection model, they fail.
                </p>
                <p className="text-gray-700">
                  Why? Because real human timing is <em>imperfectly random</em> — influenced by cognitive load, distractions, reading speed. Bot-generated "randomness" tends to be statistically too uniform, too evenly distributed. The model detects that the variance pattern is machine-generated, even though no individual action looks wrong.
                </p>
              </div>
            </section>

            {/* Axetra Technology */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                How Axetra Uses ML to Filter Your Traffic
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Axetra's traffic filtering engine is built on a multi-layer architecture designed to classify every visit in real time, with no impact on page load speed.
              </p>

              {/* Layer diagram */}
              <div className="space-y-4 mb-10">
                {[
                  {
                    step: '1',
                    title: 'High-Speed Network Filter',
                    desc: 'Every request first passes through Axetra\'s core filter built for sub-millisecond processing. This layer handles IP reputation checks, ASN classification (datacenter vs. residential), known proxy/VPN ranges, and User-Agent analysis. Results are returned before your page begins rendering.',
                    badge: 'Latency: < 5ms',
                    badgeColor: 'green',
                  },
                  {
                    step: '2',
                    title: 'Device & Browser Fingerprinting',
                    desc: 'A lightweight client-side probe collects 40+ browser and hardware signals: canvas rendering output, WebGL vendor strings, font metrics, screen geometry, and hardware concurrency. These are combined into a stable device fingerprint that persists across IP changes — so even rotating-proxy bots are tracked as the same entity.',
                    badge: 'Identifies: Headless browsers, automation tools',
                    badgeColor: 'blue',
                  },
                  {
                    step: '3',
                    title: 'Behavioral Scoring Engine',
                    desc: 'Session-level behavioral signals — interaction timing, navigation patterns, engagement depth — are fed into the ML scoring model. The model compares each session\'s behavioral profile against the learned distribution of genuine human visitors in your vertical. Sessions that deviate significantly receive elevated risk scores.',
                    badge: 'Accuracy: > 99% on known bot types',
                    badgeColor: 'purple',
                  },
                  {
                    step: '4',
                    title: 'Real-Time Event Analytics',
                    desc: 'All classification decisions and their feature vectors are logged to Axetra\'s analytical engine, enabling real-time aggregation across millions of events per second. This powers your campaign dashboard and feeds back into model retraining, so Axetra\'s detection improves as your traffic data accumulates.',
                    badge: 'Scales: Millions of events/second',
                    badgeColor: 'orange',
                  },
                  {
                    step: '5',
                    title: 'Adaptive Rule Refinement',
                    desc: 'The ML model continuously identifies new bot clusters by analyzing patterns in recent false-negative reports and emerging traffic anomalies. When a new bot technique appears across multiple campaigns, Axetra\'s system automatically tightens detection without requiring manual rule updates from users.',
                    badge: 'Updates: Continuous',
                    badgeColor: 'red',
                  },
                ].map((layer) => (
                  <div key={layer.step} className="flex gap-6 p-6 border border-gray-200 rounded-xl">
                    <div className={`flex items-center justify-center w-12 h-12 bg-primary-600 text-white rounded-full font-bold text-lg flex-shrink-0`}>
                      {layer.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-xl font-bold text-gray-900">{layer.title}</h3>
                        <span className={`px-3 py-1 bg-${layer.badgeColor}-100 text-${layer.badgeColor}-700 rounded-full text-xs font-semibold`}>
                          {layer.badge}
                        </span>
                      </div>
                      <p className="text-gray-700">{layer.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-primary-50 border border-primary-200 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">What This Means for Your Campaigns</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { stat: '< 10ms', label: 'Average classification time', sub: 'No visitor ever waits' },
                    { stat: '69+', label: 'Detection rule categories', sub: 'Covering all major bot types' },
                    { stat: '99%+', label: 'Accuracy on known bots', sub: 'Near-zero false positives' },
                  ].map((s) => (
                    <div key={s.stat} className="text-center">
                      <div className="text-4xl font-bold text-primary-600 mb-1">{s.stat}</div>
                      <div className="font-semibold text-gray-900 mb-1">{s.label}</div>
                      <div className="text-sm text-gray-600">{s.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Business Impact */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                The Business Impact of Better Bot Detection
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Better detection is not just a technical win. It translates directly into measurable business outcomes across every channel that sends paid traffic.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {[
                  {
                    icon: '💰',
                    title: 'Ad Budget Protection',
                    desc: 'Every bot click consumes real budget without any chance of conversion. ML filtering eliminates this waste, improving your effective cost-per-acquisition by removing non-converting bot traffic from the denominator.',
                  },
                  {
                    icon: '📊',
                    title: 'Clean Analytics Data',
                    desc: 'Bot traffic inflates session counts, deflates conversion rates, and distorts attribution models. With bots filtered out, your analytics reflect real user behavior — making optimization decisions reliable.',
                  },
                  {
                    icon: '🛡️',
                    title: 'Ad Account Safety',
                    desc: 'Platforms like Facebook and Google monitor the quality of traffic hitting your landing pages. High bot traffic ratios are a leading cause of policy flags and account suspensions. Filtering keeps your quality signals clean.',
                  },
                  {
                    icon: '🧪',
                    title: 'Accurate A/B Testing',
                    desc: 'Bot traffic randomizes across test variants with different distribution than real users, introducing systematic bias. Clean traffic means your test results are statistically sound and actionable.',
                  },
                  {
                    icon: '📈',
                    title: 'Better Bidding Algorithms',
                    desc: 'Automated bidding on ad platforms learns from conversion signals. When bots pollute that signal, the algorithm optimizes for the wrong audience. Remove bots, and the algorithm learns from genuine converters.',
                  },
                  {
                    icon: '🔒',
                    title: 'Content & Offer Protection',
                    desc: 'Competitors use scrapers to monitor your offers, copy your landing pages, and undercut your pricing in real time. ML detection identifies scraper traffic patterns and blocks them before they extract your data.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4 p-6 bg-gray-50 rounded-xl">
                    <span className="text-3xl flex-shrink-0">{item.icon}</span>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-700">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Rule-based vs ML comparison */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Rule-Based vs. ML-Based Detection: Head-to-Head
              </h2>

              <div className="overflow-x-auto rounded-xl border border-gray-200 mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-4 font-semibold text-gray-900">Capability</th>
                      <th className="text-center p-4 font-semibold text-gray-500">Rule-Based</th>
                      <th className="text-center p-4 font-semibold text-primary-600">ML-Based (Axetra)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      ['Blocks known bot IPs', '✅', '✅'],
                      ['Detects residential proxy bots', '❌', '✅'],
                      ['Identifies headless browser automation', '⚠️ Partial', '✅'],
                      ['Catches novel / zero-day bot scripts', '❌', '✅'],
                      ['Adapts to new evasion techniques', '❌ Manual only', '✅ Continuous'],
                      ['Analyzes behavioral timing patterns', '❌', '✅'],
                      ['Device fingerprint consistency checks', '❌', '✅'],
                      ['False positive rate on real users', 'Medium', 'Very low'],
                      ['Detection improves over time', '❌', '✅ Auto-learning'],
                      ['Works without updating rule lists', '❌', '✅'],
                    ].map(([capability, rules, ml], i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="p-4 text-gray-700 font-medium">{capability}</td>
                        <td className="p-4 text-center text-gray-600">{rules}</td>
                        <td className="p-4 text-center font-medium text-primary-700">{ml}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Getting Started */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Getting Started with ML Traffic Filtering
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Switching from rule-based to ML-powered filtering doesn't require replacing your existing infrastructure. Axetra integrates as a lightweight filter in front of your existing landing pages — no changes to your ad platform, tracking setup, or backend required.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { step: '1', title: 'Create a campaign', desc: 'Set up your campaign in the Axetra dashboard. Define what happens to bot traffic — block silently, reject the request, or log it for analysis.' },
                  { step: '2', title: 'Add the integration snippet', desc: 'Paste a single line of code into your landing page. This routes each visitor through Axetra\'s classification engine before the page renders.' },
                  { step: '3', title: 'Review your traffic quality', desc: 'Axetra\'s dashboard shows you real-time breakdowns of bot vs. human traffic, detection categories, geographic distribution, and trends over time — all with instant aggregation.' },
                  { step: '4', title: 'Let the model improve', desc: 'As your campaigns run, Axetra\'s behavioral model builds a profile of genuine visitors for your specific offers and verticals, making detection increasingly accurate over time.' },
                ].map((s) => (
                  <div key={s.step} className="flex gap-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary-600 text-white rounded-full font-bold flex-shrink-0">
                      {s.step}
                    </div>
                    <div className="pt-1">
                      <strong className="text-gray-900">{s.title}</strong>
                      <p className="text-gray-700 mt-1">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-3">Ready to See ML Bot Detection in Action?</h3>
                <p className="text-primary-100 mb-6 text-lg">
                  Join thousands of advertisers who have stopped losing budget to bots. Start filtering smarter traffic today.
                </p>
                <Link
                  to={createLink('register')}
                  className="inline-block bg-white text-primary-600 font-bold px-8 py-4 rounded-xl hover:bg-primary-50 transition-colors text-lg"
                >
                  Start Free Trial — No Credit Card Required
                </Link>
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                      <svg
                        className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-6">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Conclusion */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Conclusion</h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                The bot problem is not going away. As detection improves, bot operators invest more in evasion. The only sustainable defense is a system that learns continuously — one that doesn't need humans to write new rules every time a new bot technique emerges.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Machine learning-powered detection isn't a luxury for enterprise teams anymore. It's the baseline requirement for any serious advertiser running paid traffic in 2026. Axetra makes this technology accessible to campaigns of any size, with zero infrastructure overhead.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                The advertisers who protect their traffic quality now will have cleaner data, better-trained algorithms, and healthier ad accounts than their competitors who are still fighting yesterday's bots with yesterday's rules.
              </p>
            </section>

          </div>
        </div>
      </article>
    </>
  );
}
